import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';

import {
  accountCardCreate as accountCardCreateMutation,
  accountCardUpdate as accountCardUpdateMutation,
  accountCardsDelete as accountCardsDeleteMutation,
} from '../../graphql/mutations/accountCard';
import getAccountCardQuery from '../../graphql/queries/getAccountCard';
import useCardElementConfiguration from './useCardElementConfiguration';
import getUserForAccountCardConfigurationQuery from '../../graphql/queries/getUserForAccountCardConfiguration';
import useCardElementConfigurationHistory from './useCardElementConfigurationHistory';

const useAccountCardConfigure = ({
  accountCardId,
  accountCardAlbumFolderId,
  setSelectedCardCategory,
  onAccountCardChange,
  presetCardElementConfigurationInputs,
  storeFetchedAccountCard,
  prioritizeAccountCardId,
  linkTextElements,
  linkImageElements,
  onError,
} = {}) => {
  const [accountCard, setAccountCard] = useState(null);
  const [loadingAccountCard, setLoadingAccountCard] = useState(false);
  const [accountCardCreate] = useMutation(accountCardCreateMutation);
  const [accountCardUpdate] = useMutation(accountCardUpdateMutation);
  const [accountCardsDelete] = useMutation(accountCardsDeleteMutation);

  const { onCardElementConfigurationHistoryInsert } =
    useCardElementConfigurationHistory();

  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { data: { me: account } = {} } = useQuery(
    getUserForAccountCardConfigurationQuery,
    {
      skip: !authenticated,
    },
  );
  const { data: { accountCard: fetchedAccountCard } = {} } = useQuery(
    getAccountCardQuery,
    {
      variables: {
        id: prioritizeAccountCardId
          ? (accountCardId ?? accountCard?.id)
          : (accountCard?.id ?? accountCardId),
      },
      notifyOnNetworkStatusChange: true,
      skip: !accountCard && !accountCardId,
      onCompleted: data => {
        // If we're fetching an existing AccountCard
        // set its category.
        if (accountCardId) {
          const [cardCategory] = data.accountCard.card.card_categories.records;
          setSelectedCardCategory?.(cardCategory);
          if (!accountCard || prioritizeAccountCardId) {
            setAccountCard(data.accountCard);
          }
        }
      },
    },
  );

  // Claim the AccountCard wherever it is unclaimed and
  // an there's a logged in user.
  useEffect(() => {
    if (account && fetchedAccountCard && !fetchedAccountCard.account) {
      accountCardUpdate({
        variables: {
          input: {
            account_card_id: fetchedAccountCard.id,
            claim: true,
          },
        },
      });
    }
  }, [account, fetchedAccountCard]);

  // Notify on any AccountCard changes. Useful for
  // decoupling checkout from the configurator.
  useEffect(() => {
    onAccountCardChange?.(fetchedAccountCard);
    if (storeFetchedAccountCard) {
      setAccountCard(fetchedAccountCard);
    }
    //
  }, [fetchedAccountCard]);

  const {
    onElementConfigurationChange,
    onCardElementConfigurationDelete,
    cardElementLoading,
  } = useCardElementConfiguration({
    accountCard: fetchedAccountCard,
    presetCardElementConfigurationInputs,
    linkTextElements,
    linkImageElements,
    onAfterElementConfigurationChange: cardElementConfiguration => {
      if (cardElementConfiguration instanceof Error && onError) {
        onError(cardElementConfiguration);
        return;
      }

      // Store CardElementConfiguration changes in history.
      if (cardElementConfiguration) {
        onCardElementConfigurationHistoryInsert(cardElementConfiguration);
      }
    },
  });

  const onCardChange = async (
    input = {},
    reuseCard = true,
    targetAccountCardInput = undefined,
  ) => {
    if (loadingAccountCard) {
      return;
    }

    setLoadingAccountCard(true);

    if (accountCard && reuseCard) {
      // Update the existing account card.
      await accountCardUpdate({
        variables: {
          input: {
            account_card_id: accountCard.id,
            front_card_side_id: input.front_card_side_id,
            back_card_side_id: input.back_card_side_id,
            print_product_configuration: input.print_product_configuration
              ? {
                  print_product_id:
                    accountCard.print_product_configuration?.print_product?.id,
                  print_product_size_id:
                    accountCard.print_product_configuration?.print_product_size
                      ?.id,
                  print_product_size_mount_id:
                    accountCard.print_product_configuration
                      ?.print_product_size_mount?.id,
                  ...input.print_product_configuration,
                }
              : undefined,
          },
        },
      });

      setLoadingAccountCard(false);
      return;
    }

    let targetAccountCardId;

    if (targetAccountCardInput) {
      const targetResponse = await accountCardCreate({
        variables: {
          input: {
            ...targetAccountCardInput,
            card_element_configurations_clone: [],
          },
        },
      });
      const { account_card: targetAccountCard } =
        targetResponse?.data?.accountCardCreate ?? {};

      targetAccountCardId = targetAccountCard?.id;
    }

    // Create a new card if it does not exist yet.
    const response = await accountCardCreate({
      variables: {
        input: {
          type: input.type,
          card_id: input.card_id,
          album_folder_id: accountCardAlbumFolderId,
          front_card_side_id: input.front_card_side_id,
          back_card_side_id: input.back_card_side_id,
          album_custom_print_id: input.album_custom_print_id,
          target_account_card_id: targetAccountCardId,
          print_product_configuration: input.print_product_configuration
            ? input.print_product_configuration
            : undefined,
        },
      },
    });

    const { account_card: newAccountCard } =
      response?.data?.accountCardCreate ?? {};

    setLoadingAccountCard(false);
    setAccountCard(newAccountCard);
  };

  const onAccountCardDelete = async () => {
    if (!fetchedAccountCard) {
      return;
    }

    await accountCardsDelete({
      variables: {
        input: {
          account_card_ids: [fetchedAccountCard.id],
        },
      },
    });

    setAccountCard(null);
  };

  const onConfigurationReset = () => {
    setAccountCard(null);
  };

  const onUseExistingCard = existingAccountCard => {
    setAccountCard(existingAccountCard);
    const [cardCategory] = existingAccountCard.card.card_categories.records;
    setSelectedCardCategory?.(cardCategory);
  };

  return {
    loadingAccountCard,
    onCardChange,
    accountCard: fetchedAccountCard,
    onElementConfigurationChange,
    onAccountCardDelete,
    onConfigurationReset,
    authenticated,
    cardElementLoading,
    onUseExistingCard,
    onCardElementConfigurationDelete,
  };
};

export default useAccountCardConfigure;
